import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Globe2,
  Inbox,
  Link2,
  Mail,
  MonitorSmartphone,
  Phone,
  Settings2,
  Siren,
  Slack,
  Users,
  Video,
} from "lucide-react";
import { formatExactTime, formatTimeAgo } from "@/components/agent/display/utils/formatters";
import { useConversationAnnotations } from "@/hooks/conversation/useConversationAnnotations";
import { isListened } from "@/lib/conversation/listenedCall";
import { isListenedEmail } from "@/lib/conversation/listenedEmail";
import { isRecordedMeeting } from "@/lib/conversation/recordedMeeting";
import { cn } from "@/lib/utils";
import { Conversation, ConversationChannel, ConversationType } from "@/types/conversation/conversation";

export interface ConversationSignals {
  convTime: string;
  timeAgo: string;
  exactTime: string;
  hasValidTime: boolean;
  durationSeconds: number | null;
  msgCount: number | null;
  sentimentScore: number | null;
  csatScore: number | null;
  agentTalkPct: number | null;
  custTalkPct: number | null;
  silencePct: number | null;
  statusBadge: { label: string; tone: "success" | "ended" } | null;
  brandColor: string;
  secondaryColor: string;
  hasGradient: boolean;
  gradientAngle: number;
  logoUrl: string | undefined;
  primaryTopic: string | null;
  summaryPreview: string | null;
  channel: ConversationChannel;
  conversationType: ConversationType;
  attentionBadge: { label: string; severity: "low" | "medium" | "high" | null; tone?: "accent" } | null;
  /** An unhandled listened email or call the genie triaged as urgent or high priority. */
  isUrgent: boolean;
  /**
   * Best-available customer identity for person-first display: captured
   * name, else email, else phone (incl. caller ID on phone calls). Null
   * when the conversation is anonymous.
   */
  customerLabel: string | null;
}

export const extractConversationSignals = (conversation: Conversation): ConversationSignals => {
  const brandColor = conversation.agent?.branding?.primaryColor || "#4E9CFF";
  const secondaryColor = conversation.agent?.branding?.secondaryColor || brandColor;
  const logoUrl =
    conversation.agent?.branding?.logoUrl || conversation.agent?.branding?.customAvatarUrl;
  const hasGradient = conversation.agent?.branding?.gradientEnabled !== false;
  const gradientAngle = conversation.agent?.branding?.gradientAngle || 135;

  const convTime = (() => {
    if (conversation.details?.conversation_date) {
      return conversation.details.conversation_date;
    }
    if (conversation.metadata?.start_time) {
      return new Date(conversation.metadata.start_time * 1000).toISOString();
    }
    return "";
  })();

  const timeAgo = formatTimeAgo(convTime);
  const exactTime = formatExactTime(convTime);
  const hasValidTime = timeAgo !== "Unknown" && exactTime !== "Unknown";

  const status = conversation.details?.status;
  const successful = conversation.metadata?.call_successful;
  const statusBadge: ConversationSignals["statusBadge"] =
    successful === "successful" || status === "complete"
      ? { label: "Completed", tone: "success" }
      : successful === "unsuccessful" || status === "ended_early"
        ? { label: "Ended", tone: "ended" }
        : null;

  const aiResult = conversation.details?.analysis_result;
  const sentimentScore = numericOrNull(aiResult?.sentiment_analysis?.overall_sentiment_score);
  const csatScore = numericOrNull(aiResult?.sentiment_analysis?.customer_satisfaction_score);
  const agentTalkPct = numericOrNull(aiResult?.talk_metrics?.agent_talk_percentage);
  const custTalkPct = numericOrNull(aiResult?.talk_metrics?.customer_talk_percentage);
  const silencePct = numericOrNull(aiResult?.talk_metrics?.silence_percentage);

  const topicsRaw = Array.isArray(conversation.details?.topics)
    ? conversation.details?.topics
    : [];
  const primaryTopic =
    typeof topicsRaw?.[0] === "string" && topicsRaw[0].trim().length > 0
      ? topicsRaw[0].trim()
      : null;
  const rawSummary =
    (typeof conversation.details?.summary === "string"
      ? conversation.details.summary
      : null) ||
    (typeof aiResult?.brief_summary === "string" ? aiResult.brief_summary : null);
  const summaryPreview = rawSummary ? rawSummary.trim() : null;
  const channel = getConversationChannel(conversation);
  const listenedAttention = getListenedAttention(conversation);
  const escalation = conversation.details?.escalation;
  const needsAttention =
    escalation || aiResult?.needs_human_attention === true;
  const severity = isEscalationSeverity(escalation?.severity)
    ? escalation.severity
    : isEscalationSeverity(aiResult?.escalation_severity)
      ? aiResult.escalation_severity
      : null;
  const attentionBadge: ConversationSignals["attentionBadge"] = isListened(conversation)
    ? listenedAttention
    : needsAttention
      ? { label: severity === "high" ? "Urgent" : "Escalated", severity }
      : null;

  return {
    convTime,
    timeAgo,
    exactTime,
    hasValidTime,
    durationSeconds:
      numericOrNull(conversation.details?.duration) ??
      numericOrNull(conversation.metadata?.duration_seconds),
    msgCount: numericOrNull(conversation.metadata?.message_count),
    sentimentScore,
    csatScore,
    agentTalkPct,
    custTalkPct,
    silencePct,
    statusBadge,
    brandColor,
    secondaryColor,
    hasGradient,
    gradientAngle,
    logoUrl,
    primaryTopic,
    summaryPreview,
    channel,
    conversationType: getConversationType(conversation),
    attentionBadge,
    isUrgent: listenedAttention?.severity === "high",
    customerLabel: getCustomerLabel(conversation),
  };
};

const LISTENED_ATTENTION: Record<"urgent" | "high", NonNullable<ConversationSignals["attentionBadge"]>> = {
  urgent: { label: "Urgent", severity: "high" },
  high: { label: "High priority", severity: "high" },
};

/** A listened email or call needs attention until the team marks it handled. */
const getListenedAttention = (conversation: Conversation): ConversationSignals["attentionBadge"] => {
  if (!isListened(conversation) || conversation.handled_at || !conversation.attention) return null;
  if (conversation.attention === "urgent" || conversation.attention === "high") {
    return LISTENED_ATTENTION[conversation.attention];
  }
  return {
    label: isListenedEmail(conversation.type) ? "Needs a reply" : "Needs a follow-up",
    severity: "low",
    tone: "accent",
  };
};

/**
 * Resolve the best identity we have for the person on the other side.
 * Order: captured name → email → phone from insights → caller ID on
 * phone calls. Most conversations are anonymous today; this surfaces
 * whatever the call DID capture so rows read person-first when possible.
 */
export const getCustomerLabel = (conversation: Conversation): string | null => {
  const leadInfo = (conversation.details?.lead_info ?? {}) as Record<string, unknown>;
  const customerData = ((conversation.details?.analysis_result?.customer_data ??
    conversation.metadata?.customer_data) ?? {}) as Record<string, unknown>;

  const name =
    cleanIdentity(leadInfo.full_name) ||
    cleanIdentity(leadInfo.name) ||
    cleanIdentity(leadInfo.first_name) ||
    cleanIdentity(customerData.name);
  if (name) return name;

  const email = cleanIdentity(leadInfo.email) || cleanIdentity(customerData.email);
  if (email) return email;

  const phone =
    cleanIdentity(leadInfo.phone) ||
    cleanIdentity(leadInfo.phone_number) ||
    cleanIdentity(customerData.phone) ||
    cleanIdentity(
      (conversation.metadata?.phone_call as Record<string, unknown> | undefined)
        ?.external_number,
    );
  return phone;
};

const NON_IDENTITIES = new Set([
  "anonymous",
  "unknown",
  "n/a",
  "na",
  "none",
  "not provided",
  "not captured",
  "null",
  "undefined",
]);

const cleanIdentity = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed.length === 0 || NON_IDENTITIES.has(trimmed.toLowerCase())) return null;
  return trimmed;
};

export const getConversationType = (conversation: Conversation): ConversationType => {
  if (conversation.type === "setup" || conversation.type === "mailbox") return conversation.type;
  return "regular";
};

/** Prefer an explicit integration source, then fall back to known call metadata. */
export const getConversationChannel = (conversation: Conversation): ConversationChannel => {
  const metadata = conversation.metadata as Record<string, unknown> | null;

  // Recordings are meetings, except phone calls a Listener Genie recorded on its own number.
  if (isRecordedMeeting(conversation.type)) return metadata?.source === "phone" ? "phone" : "meeting";

  if (metadata?.phone_call) return "phone";

  const source = [metadata?.channel, metadata?.source, metadata?.origin]
    .find((value): value is string => typeof value === "string")
    ?.toLowerCase();

  if (source === "phone" || source === "call" || source === "voice") return "phone";
  if (source === "email" || source === "mail") return "email";
  if (source === "app" || source === "mobile" || source === "native") return "app";
  if (source === "link" || source === "url" || source === "qr") return "link";
  if (source === "slack") return "slack";
  if (source === "teams") return "teams";
  return "web";
};

const isEscalationSeverity = (value: unknown): value is "low" | "medium" | "high" =>
  value === "low" || value === "medium" || value === "high";

const CHANNEL_META: Record<ConversationChannel, { label: string; Icon: typeof Phone }> = {
  phone: { label: "Call", Icon: Phone },
  web: { label: "Website", Icon: Globe2 },
  link: { label: "Link", Icon: Link2 },
  app: { label: "App", Icon: MonitorSmartphone },
  email: { label: "Email", Icon: Mail },
  meeting: { label: "Meeting", Icon: Video },
  slack: { label: "Slack", Icon: Slack },
  teams: { label: "Teams", Icon: Users },
};

export const CHANNEL_FILTER_OPTIONS: { value: ConversationChannel; label: string; Icon: typeof Phone }[] = (
  Object.keys(CHANNEL_META) as ConversationChannel[]
).map((value) => ({ value, ...CHANNEL_META[value] }));

export const getChannelLabel = (channel: ConversationChannel): string =>
  CHANNEL_META[channel].label;

export const ConversationChannelPill = ({ channel }: { channel: ConversationChannel }) => {
  const { label, Icon } = CHANNEL_META[channel];
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600" title={label}>
      <Icon className="w-3.5 h-3.5 text-genie-primary" aria-hidden />
      {label}
    </span>
  );
};

const CONVERSATION_TYPE_META: Record<
  ConversationType,
  { label: string; Icon: typeof Phone; color: string; bg: string }
> = {
  regular: { label: "Conversation", Icon: CheckCircle2, color: "#4E9CFF", bg: "rgba(78,156,255,0.08)" },
  setup: { label: "Setup", Icon: Settings2, color: "#64748B", bg: "rgba(100,116,139,0.08)" },
  mailbox: { label: "Mailbox", Icon: Inbox, color: "#FFBB33", bg: "rgba(255,187,51,0.12)" },
};

export const CONVERSATION_TYPE_FILTER_OPTIONS: { value: ConversationType; label: string }[] = (
  Object.keys(CONVERSATION_TYPE_META) as ConversationType[]
).map((value) => ({ value, label: CONVERSATION_TYPE_META[value].label }));

export const ConversationTypePill = ({ type }: { type: ConversationType }) => {
  const { label, Icon, color, bg } = CONVERSATION_TYPE_META[type];
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold"
      style={{ color, background: bg }}
      title={label}
    >
      <Icon className="w-3 h-3" aria-hidden />
      {label}
    </span>
  );
};

export const ConversationAttentionPill = ({
  attention,
}: {
  attention: ConversationSignals["attentionBadge"];
}) => {
  if (!attention) return <span className="text-xs text-gray-400">—</span>;

  const isAccent = attention.tone === "accent";
  const Icon = isAccent ? AlertTriangle : Siren;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold",
        isAccent
          ? "bg-genie-accent/15 text-genie-accent-dark"
          : attention.severity === "high"
            ? "bg-genie-secondary text-white"
            : "bg-genie-secondary/10 text-genie-secondary",
      )}
    >
      <Icon className="w-3 h-3" aria-hidden />
      {attention.label}
    </span>
  );
};

const numericOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const sentimentTone = (score: number) => {
  if (score >= 0.4) return { color: "#4E9CFF", label: "positive" };
  if (score <= -0.4) return { color: "#D6409F", label: "negative" };
  if (score < -0.1) return { color: "#FF4DB8", label: "mixed" };
  return { color: "#64748B", label: "neutral" };
};

const csatTone = (pct: number) => {
  if (pct >= 75) return { color: "#4E9CFF" };
  if (pct >= 50) return { color: "#64748B" };
  if (pct >= 30) return { color: "#FF4DB8" };
  return { color: "#D6409F" };
};

export const formatSentiment = (score: number) =>
  `${score >= 0 ? "+" : ""}${score.toFixed(2)}`;

export const SentimentPill = ({ score, dense = false }: { score: number; dense?: boolean }) => {
  const tone = sentimentTone(score);
  return (
    <span
      className={`inline-flex items-center font-semibold ${
        dense ? "text-xs" : "text-sm"
      }`}
      style={{ color: tone.color }}
    >
      {formatSentiment(score)}
    </span>
  );
};

export const CsatPill = ({ score, dense = false }: { score: number; dense?: boolean }) => {
  const tone = csatTone(score);
  return (
    <span
      className={`inline-flex items-center font-semibold ${
        dense ? "text-xs" : "text-sm"
      }`}
      style={{ color: tone.color }}
    >
      {Math.round(score)}%
    </span>
  );
};

/**
 * Owner-side annotations row: follow-up state + custom tags.
 * Phase 1 storage is localStorage, so this reads per-browser only.
 *
 * Set `hideEmpty` to render nothing when there are no annotations, instead
 * of an em-dash placeholder. Useful inline where the surrounding layout
 * already implies "no annotations" by absence.
 */
export const ConversationAnnotationPills = ({
  conversationId,
  dense = false,
  hideEmpty = false,
}: {
  conversationId: string;
  dense?: boolean;
  hideEmpty?: boolean;
}) => {
  const { annotations } = useConversationAnnotations(conversationId);
  const { followUpStatus, tags } = annotations;

  if (followUpStatus === "none" && tags.length === 0) {
    if (hideEmpty) return null;
    return (
      <span className={`text-gray-400 ${dense ? "text-xs" : "text-sm"}`}>—</span>
    );
  }

  const followUpPill = (() => {
    if (followUpStatus === "needs") {
      return (
        <span
          className={`inline-flex items-center gap-1 rounded-full border font-semibold ${
            dense ? "text-[11px] px-1.5 py-0.5" : "text-xs px-2 py-0.5"
          }`}
          style={{
            color: "#FF4DB8",
            borderColor: "rgba(255,77,184,0.5)",
            background: "rgba(255,77,184,0.08)",
          }}
        >
          <AlertCircle className={dense ? "w-3 h-3" : "w-3.5 h-3.5"} />
          Needs follow-up
        </span>
      );
    }
    if (followUpStatus === "followed_up") {
      return (
        <span
          className={`inline-flex items-center gap-1 rounded-full border font-semibold ${
            dense ? "text-[11px] px-1.5 py-0.5" : "text-xs px-2 py-0.5"
          }`}
          style={{
            color: "#4E9CFF",
            borderColor: "rgba(78,156,255,0.5)",
            background: "rgba(78,156,255,0.08)",
          }}
        >
          <CheckCircle2 className={dense ? "w-3 h-3" : "w-3.5 h-3.5"} />
          Followed up
        </span>
      );
    }
    return null;
  })();

  return (
    <div className={`flex items-center flex-wrap ${dense ? "gap-1" : "gap-1.5"}`}>
      {followUpPill}
      {tags.map((tag) => (
        <TagChip key={tag} tag={tag} dense={dense} />
      ))}
    </div>
  );
};

const TAG_TONES: Record<string, { color: string; bg: string; border: string }> = {
  Priority: {
    color: "#D6409F",
    bg: "rgba(214,64,159,0.08)",
    border: "rgba(214,64,159,0.4)",
  },
  Complaint: {
    color: "#D6409F",
    bg: "rgba(214,64,159,0.08)",
    border: "rgba(214,64,159,0.4)",
  },
  "Good lead": {
    color: "#4E9CFF",
    bg: "rgba(78,156,255,0.08)",
    border: "rgba(78,156,255,0.4)",
  },
  "Follow up": {
    color: "#FF4DB8",
    bg: "rgba(255,77,184,0.08)",
    border: "rgba(255,77,184,0.4)",
  },
  Showcase: {
    color: "#FFBB33",
    bg: "rgba(255,187,51,0.12)",
    border: "rgba(255,187,51,0.5)",
  },
};

const DEFAULT_TAG_TONE = {
  color: "#64748B",
  bg: "rgba(100,116,139,0.08)",
  border: "rgba(100,116,139,0.35)",
};

const TagChip = ({ tag, dense }: { tag: string; dense?: boolean }) => {
  const tone = TAG_TONES[tag] ?? DEFAULT_TAG_TONE;
  return (
    <span
      className={`inline-flex items-center rounded-full border font-medium ${
        dense ? "text-[11px] px-1.5 py-0.5" : "text-xs px-2 py-0.5"
      }`}
      style={{ color: tone.color, background: tone.bg, borderColor: tone.border }}
    >
      {tag}
    </span>
  );
};

